import { ApiService } from '../services/api'
import { AppState } from '../stores/app-state'
import { DocumentManager } from './document-manager'
import { DataManager } from './data-manager'
import { SelectionManager } from './selection-manager'
import { Toast } from '../components/toast'
import { PDFViewer } from '../components/pdf-viewer-legacy'
import { PDFDocumentViewer } from '../components/pdf-document-viewer'
import { DocumentViewerFactory } from '../components/document-viewer-factory'
import { ImageThumbnailGenerator } from '../services/thumbnail-generator'
import type { DocumentViewer } from '../interfaces/document-viewer'

export class ModalManager {
  private apiService: ApiService
  private appState: AppState
  private documentManager: DocumentManager
  private dataManager: DataManager
  // private selectionManager: SelectionManager // Not used in modal operations
  private toast: Toast
  private pdfViewer: PDFViewer | null = null
  private documentViewer: DocumentViewer | null = null // New refactored viewer
  
  // Shared thumbnail generator for images
  private imageThumbnailGenerator: ImageThumbnailGenerator | null = null

  constructor(
    apiService: ApiService,
    appState: AppState,
    documentManager: DocumentManager,
    dataManager: DataManager,
    _selectionManager: SelectionManager, // Prefix with underscore to indicate unused
    toast: Toast
  ) {
    this.apiService = apiService
    this.appState = appState
    this.documentManager = documentManager
    this.dataManager = dataManager
    // this.selectionManager = selectionManager // Not used in modal operations
    this.toast = toast
    
    // Initialize shared thumbnail generator for images
    this.imageThumbnailGenerator = new ImageThumbnailGenerator({
      apiService: this.apiService,
      updateSelectionWithRetry: this.updateSelectionWithRetry.bind(this),
      refreshGallery: this.refreshImageSelectionsGallery.bind(this)
    }, this.appState)
  }

  public setPDFViewer(pdfViewer: PDFViewer | null): void {
    this.pdfViewer = pdfViewer
  }

  public openDocumentUploadModal(): void {
    const modal = new (window as any).Modal({
      title: '📄 Select From Documents',
      size: 'full',
      closeOnBackdrop: false,
      closeOnEscape: true,
      onClose: () => {
        // Re-render the main step to show updated selections
        const container = document.getElementById('step-content')
        if (container) {
          // Trigger re-render by dispatching a custom event
          window.dispatchEvent(new CustomEvent('modal-closed', { detail: { type: 'document-upload' } }))
        }
      }
    })
    
    const modalContent = this.createDocumentUploadModalContent()
    modal.open(modalContent)
    
    // CRITICAL FIX: Set modal opened timestamp to track new selections
    setTimeout(() => {
      const modalElement = document.querySelector('.modal-backdrop')
      if (modalElement) {
        (modalElement as any).modalOpenedTime = Date.now()
        console.log('🕐 Modal opened timestamp set:', (modalElement as any).modalOpenedTime)
      }
    }, 100) // Small delay to ensure modal is fully rendered
  }

  public openManualEntryModal(prefilledText?: string, documentId?: string): void {
    console.log('🎯 ModalManager.openManualEntryModal called with:', { prefilledText: prefilledText?.substring(0, 50) + '...', documentId, isEditing: !!prefilledText && !!documentId })
    const isEditing = !!prefilledText && !!documentId
    const modal = new (window as any).Modal({
      title: isEditing ? '✏️ Edit Manual Text Entry' : '✍️ Manual Text Entry',
      size: 'large',
      closeOnBackdrop: false,
      closeOnEscape: true,
      onClose: () => {
        // Re-render the main step to show updated selections
        const container = document.getElementById('step-content')
        if (container) {
          // Trigger re-render by dispatching a custom event
          window.dispatchEvent(new CustomEvent('modal-closed', { detail: { type: 'manual-entry' } }))
        }
      }
    })
    
    const modalContent = this.createManualEntryModalContent(modal, prefilledText, documentId)
    modal.open(modalContent)
  }

  private createDocumentUploadModalContent(): HTMLElement {
    const modalContentDiv = document.createElement('div')
    modalContentDiv.className = 'modal-pdf-viewer'
    
    modalContentDiv.innerHTML = `
      <div class="modal-pdf-controls">
        <div class="flex items-center space-x-4">
          <button class="btn btn-outline" id="modal-upload-document">
            📄 Upload Document
          </button>
          <div id="modal-document-selector" class="flex-1">
            <!-- Document selector will be inserted here -->
          </div>
        </div>
        
        <!-- Image Upload Options (hidden by default) -->
        <div id="modal-image-options" class="hidden mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div class="text-sm font-medium text-blue-900 mb-3">
            🖼️ Image Uploaded - Choose how to proceed:
          </div>
          <div class="flex space-x-3">
            <button class="btn btn-primary" id="modal-use-full-image">
              📄 Use Full Image
            </button>
            <button class="btn btn-outline" id="modal-select-from-image">
              ✂️ Select from Image
            </button>
          </div>
          <div class="text-xs text-blue-700 mt-2">
            <strong>Use Full Image:</strong> Process the entire image as one selection<br>
            <strong>Select from Image:</strong> Choose specific regions to extract
          </div>
        </div>
      </div>
      
      <div class="modal-pdf-content">
        <div class="modal-pdf-canvas">
          <div id="modal-pdf-viewer">
            <!-- PDF viewer will render its own controls and content here -->
          </div>
        </div>
        
        <div class="modal-pdf-sidebar">
          <div class="modal-pdf-sidebar-header">
            <h3 class="font-semibold text-gray-900">Selected Regions</h3>
          </div>
          <div class="modal-pdf-sidebar-content">
            <div id="modal-selections-gallery">
              <div class="text-center py-8 text-gray-500">
                <div class="text-4xl mb-3">✂️</div>
                <h4 class="font-medium mb-2">No Selections Yet</h4>
                <p class="text-sm">Click and drag on the document to select regions</p>
              </div>
            </div>
          </div>
          <div class="modal-pdf-sidebar-footer border-t border-gray-200 p-4">
            <div class="space-y-3">
              <div class="flex items-center justify-center">
                <div class="text-sm font-medium text-gray-700">
                  <span id="modal-selections-count">0 selections</span>
                </div>
              </div>
              <div class="flex space-x-3">
                <button class="btn btn-outline" id="modal-cancel-btn">
                  Cancel
                </button>
                <button class="btn btn-primary flex-1" id="modal-accept-btn">
                  ✓ Accept Selections
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `
    
    // Initialize components after DOM is ready
    setTimeout(() => {
      this.initializeModalComponents(modalContentDiv)
    }, 100)
    
    return modalContentDiv
  }

  private createManualEntryModalContent(modal?: any, prefilledText?: string, documentId?: string): HTMLElement {
    console.log('🎯 createManualEntryModalContent called with:', { prefilledText: prefilledText?.substring(0, 50) + '...', documentId, isEditing: !!prefilledText && !!documentId })
    const isEditing = !!prefilledText && !!documentId
    const modalContentDiv = document.createElement('div')
    modalContentDiv.className = 'space-y-6'
    
    const saveButtonHtml = `<button class="btn btn-primary" id="modal-manual-save" data-document-id="${documentId || ''}">
            ${isEditing ? '💾 Update Descriptions' : '💾 Save Descriptions'}
          </button>`
    
    console.log('🎯 Save button HTML being created:', saveButtonHtml)
    console.log('🎯 documentId value in HTML:', documentId)
    
    modalContentDiv.innerHTML = `
      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            ${isEditing ? 'Edit Metes and Bounds Descriptions' : 'Enter Metes and Bounds Descriptions'}
          </label>
          <textarea 
            id="manual-text-input"
            class="w-full h-64 p-3 border border-gray-300 rounded-md resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="${isEditing ? 'Edit your metes and bounds descriptions...' : 'Type or paste your metes and bounds descriptions here...\n\nExample:\nBeginning at the Southeast corner of the tract, thence N43°15\'00"E 351.85 feet to a point, thence following a curve to the left with a radius of 306 feet for an arc distance of 249.47 feet...'}"
          >${prefilledText || ''}</textarea>
        </div>
        
        <div class="flex justify-end space-x-3">
          <button class="btn btn-outline" id="modal-manual-cancel">
            Cancel
          </button>
          ${saveButtonHtml}
        </div>
      </div>
    `
    
    // Setup event listeners
    setTimeout(() => {
      this.setupManualEntryEventListeners(modalContentDiv, modal)
    }, 100)
    
    return modalContentDiv
  }

  private initializeModalComponents(modalContent: HTMLElement): void {
    // Initialize file upload in modal
    const uploadBtn = modalContent.querySelector('#modal-upload-document')
    uploadBtn?.addEventListener('click', () => {
      // Create file input
      const fileInput = document.createElement('input')
      fileInput.type = 'file'
      fileInput.accept = 'application/pdf,.pdf,image/*,.txt,.doc,.docx'
      fileInput.style.display = 'none'
      
      fileInput.addEventListener('change', async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0]
        if (file) {
          console.log('📁 File selected for upload:', {
            name: file.name,
            type: file.type,
            size: file.size
          })
          
          await this.documentManager.handleModalFileUpload(file)
          
          // CRITICAL FIX: Show image options if an image was uploaded
          if (file.type.startsWith('image/')) {
            console.log('🖼️ Image file detected, showing upload options')
            this.showImageUploadOptions(modalContent)
          } else {
            console.log('📄 Non-image file detected, not showing image options')
          }
        }
      })
      
      document.body.appendChild(fileInput)
      fileInput.click()
      document.body.removeChild(fileInput)
    })
    
    // Initialize document selector
    const selectorContainer = modalContent.querySelector('#modal-document-selector')
    if (selectorContainer) {
      this.initializeModalDocumentSelector(selectorContainer as HTMLElement)
    }
    
    // Setup modal footer button listeners
    this.setupModalFooterListeners(modalContent)
  }

  private initializeModalDocumentSelector(container: HTMLElement): void {
    this.refreshModalDocumentSelector(container)
  }

  /**
   * Refresh the modal document selector with current project documents
   */
  public refreshModalDocumentSelector(container?: HTMLElement, selectedDocumentId?: string): void {
    const selectorContainer = container || document.querySelector('#modal-document-selector')
    if (!selectorContainer) {
      console.warn('Modal document selector container not found')
      return
    }

    const allDocs = this.appState.getProjectDocuments()
    // Filter out manual text entries - only show PDFs and images for selection
    const docs = allDocs.filter(doc => doc.document_type !== 'manual')
    console.log('📋 Refreshing document selector with docs:', docs.length, docs, 'filtered from', allDocs.length, 'total')
    
    if (docs.length === 0) {
      console.log('⚠️ No selectable documents found, showing empty state')
      selectorContainer.innerHTML = `
        <div class="text-sm text-gray-500 italic">
          No documents uploaded yet
        </div>
      `
      return
    }
    
    console.log('📝 Creating/updating document selector dropdown')
    selectorContainer.innerHTML = `
      <select class="w-full p-2 border border-gray-300 rounded-md" id="modal-document-select">
        <option value="">Select a document...</option>
        ${docs.map(doc => {
          const icon = doc.document_type === 'image' ? '🖼️' : '📄'
          return `<option value="${doc.id}" ${selectedDocumentId === doc.id ? 'selected' : ''}>${icon} ${doc.original_filename}</option>`
        }).join('')}
      </select>
    `
    
    const select = selectorContainer.querySelector('#modal-document-select') as HTMLSelectElement
    console.log('📝 Document selector created/updated, options:', select?.options?.length)
    
    // Remove existing event listeners by cloning the element
    const newSelect = select.cloneNode(true) as HTMLSelectElement
    select.parentNode?.replaceChild(newSelect, select)
    
    newSelect.addEventListener('change', async (e) => {
      const pdfId = (e.target as HTMLSelectElement).value
      console.log('📝 Document selected from dropdown:', pdfId)
      if (pdfId) {
        const documentInfo = await this.apiService.getDocumentInfo(pdfId)
        const pdfFile = {
          id: pdfId,
          project_id: documentInfo.project_id,
          filename: documentInfo.original_filename,
          pageCount: documentInfo.page_count || 1,
          dimensions: documentInfo.dimensions || { width: 0, height: 0 }
        }
        
        this.appState.setCurrentFile(pdfFile)
        
        // Add a small delay to ensure modal DOM is fully rendered
        setTimeout(async () => {
          await this.initializeModalPDFViewer(pdfFile, false, true) // Pass forceReload=true for PDF switching
        }, 50)
      }
    })
  }

  public async initializeModalImageViewer(imageFile: any, skipAutoSelections: boolean = false, forceReload: boolean = false): Promise<void> {
    console.log('🖼️ initializeModalImageViewer called with:', { imageFile: imageFile?.filename, skipAutoSelections, forceReload })
    
    const viewerContainer = document.querySelector('#modal-pdf-viewer')
    const galleryContainer = document.querySelector('#modal-selections-gallery')
    
    if (viewerContainer) {
      // Use the new ImageViewer class instead of custom implementation
      const { ImageViewer } = await import('../components/image-viewer')
      
      // Clear existing content
      viewerContainer.innerHTML = ''
      
      // Create new image viewer instance
      const imageViewer = new ImageViewer(viewerContainer as HTMLElement, this.apiService)
      
      // Load the image document
      await imageViewer.loadImage(imageFile)
      
      // Load existing selections if not skipping
      if (!skipAutoSelections) {
        await imageViewer.loadExistingSelections(imageFile.id)
      }
      
      // Initialize selections gallery
      imageViewer.initializeSelectionsGallery()
      
      // Store reference for later use
      ;(viewerContainer as any).imageViewer = imageViewer
      
      console.log('✅ Image viewer initialized with new ImageViewer class')
    }
    
    // Initialize selections gallery if available
    if (galleryContainer) {
      this.initializeImageSelectionsGallery(galleryContainer as HTMLElement, imageFile.id)
    }
  }

  public async initializeModalPDFViewer(pdfFile: any, skipAutoSelections: boolean = false, forceReload: boolean = false): Promise<void> {
    console.log('🎬 initializeModalPDFViewer called with:', { pdfFile: pdfFile?.filename, skipAutoSelections, forceReload })
    
    // Check if this is an image file - if so, show image viewer
    if (pdfFile && (pdfFile.isImage || pdfFile.document_type === 'image' || /\.(jpg|jpeg|png|gif|bmp|tiff|webp)$/i.test(pdfFile.original_filename || pdfFile.filename))) {
      await this.initializeModalImageViewer(pdfFile, skipAutoSelections, forceReload)
      return
    }
    
    // Use the new factory pattern for document viewers
    await this.initializeModalDocumentViewer(pdfFile, skipAutoSelections, forceReload)
  }
  
  /**
   * Initialize document viewer using the new factory pattern
   */
  public async initializeModalDocumentViewer(documentFile: any, skipAutoSelections: boolean = false, forceReload: boolean = false): Promise<void> {
    console.log('🎬 initializeModalDocumentViewer called with:', { documentFile: documentFile?.filename, skipAutoSelections, forceReload })
    
    const viewerContainer = document.querySelector('#modal-pdf-viewer')
    const galleryContainer = document.querySelector('#modal-selections-gallery')
    
    // Debug: Check what modal containers exist
    const allModalContainers = document.querySelectorAll('[id*="modal"]')
    console.log('🔍 All modal containers found:', Array.from(allModalContainers).map(el => el.id))
    
    console.log('🎬 Initializing document viewer, containers found:', {
      viewer: !!viewerContainer,
      gallery: !!galleryContainer,
      viewerElement: viewerContainer,
      galleryElement: galleryContainer
    })
    
    if (viewerContainer) {
      // Gallery container might be optional for some use cases
      if (!galleryContainer) {
        console.warn('⚠️ Gallery container not found, continuing without gallery')
      }
      // Clear existing content from the viewer container
      console.log('🧹 Clearing existing viewer content')
      viewerContainer.innerHTML = ''
      
      try {
        console.log('🔄 Creating document viewer using factory pattern')
        
        // Determine document type
        const documentType = DocumentViewerFactory.getViewerTypeFromDocument(documentFile)
        console.log('📄 Document type detected:', documentType)
        
        // Create viewer using factory
        const viewerConfig = {
          container: viewerContainer as HTMLElement,
          apiService: this.apiService,
          appState: this.appState
        }
        
        this.documentViewer = DocumentViewerFactory.createViewer(documentType, viewerConfig)
        console.log('✅ Document viewer created:', this.documentViewer.constructor.name)
        
        // Set up gallery container ID if available
        if (galleryContainer) {
          const modalGallery = galleryContainer as HTMLElement
          modalGallery.id = 'selections-gallery-container-main'
        }
        
        // Load the document
        console.log('📄 Loading document in viewer:', documentFile.filename)
        await this.documentViewer.loadDocument(documentFile)
        
        // Connect to gallery if available
        if (galleryContainer && this.documentViewer.initializeSelectionsGallery) {
          console.log('🖼️ Initializing selections gallery')
          this.documentViewer.initializeSelectionsGallery()
        } else {
          console.log('⚠️ Skipping gallery initialization - container not found or method not available')
        }
        
        // Handle edit mode for PDF documents
        if (skipAutoSelections && documentType === 'pdf') {
          console.log('📋 Edit mode: ensuring selections are loaded for highlighting')
          // Use requestAnimationFrame for immediate execution after DOM updates
          requestAnimationFrame(() => {
            if (this.documentViewer) {
              const currentSelections = this.documentViewer.getSelections()
              console.log(`📊 Selections available for edit mode: ${currentSelections.length}`)
              
              // Check if we're editing a specific selection
              const editingSelectionId = (window as any).editingSelectionId
              if (editingSelectionId) {
                console.log(`🎯 Highlighting editing selection: ${editingSelectionId}`)
                // Find and highlight the specific selection
                const editingSelection = currentSelections.find(s => s.id === editingSelectionId)
                if (editingSelection) {
                  console.log('✅ Found editing selection, navigating to location...')
                  // Navigate to the selection to show where it is
                  if (this.documentViewer && typeof this.documentViewer.goToSelection === 'function') {
                    this.documentViewer.goToSelection(editingSelectionId)
                  }
                } else {
                  console.warn('⚠️ Editing selection not found in loaded selections')
                }
                // Clear the editing selection ID
                delete (window as any).editingSelectionId
              }
            }
          })
        }
        
        // For backward compatibility, also set the old pdfViewer reference if it's a PDF
        if (documentType === 'pdf' && this.documentViewer instanceof PDFDocumentViewer) {
          this.pdfViewer = this.documentViewer as any // Type assertion for backward compatibility
          this.documentManager.setPDFViewer(this.pdfViewer)
        }
        
      } catch (error) {
        console.error('❌ Failed to initialize document viewer:', error)
        viewerContainer.innerHTML = `
          <div class="text-center py-16 text-red-500">
            <h3 class="text-xl font-medium mb-2">Document Viewer Initialization Failed</h3>
            <p class="text-gray-600">Please try refreshing the page or contact support if the issue persists.</p>
            <button class="mt-4 btn btn-primary" onclick="location.reload()">Refresh Page</button>
          </div>
        `
        throw error
      }
    } else {
      console.error('❌ Modal viewer container not found')
      throw new Error('Modal viewer container not found')
    }
  }
  
  /**
   * Get the current document viewer
   */
  public getDocumentViewer(): DocumentViewer | null {
    return this.documentViewer
  }
  
  /**
   * Get the current PDF viewer (for backward compatibility)
   */
  public getPDFViewer(): PDFViewer | null {
    return this.pdfViewer
  }

  // @ts-ignore - Legacy method, replaced by ImageViewer class
  private async loadImageInViewer(imageFile: any, viewerContainer: HTMLElement): Promise<void> {
    const imageElement = viewerContainer.querySelector('#image-display') as HTMLImageElement
    if (!imageElement) return

    try {
      console.log('🖼️ Loading image using authenticated API service')
      
      // Use authenticated API service to download image as blob
      const imageBlob = await this.apiService.downloadDocumentAsBlob(imageFile.id)
      const objectUrl = URL.createObjectURL(imageBlob)
      
      // Load image with proper error handling
      imageElement.onload = () => {
        console.log('✅ Image loaded successfully')
        // Set initial zoom to fit container
        this.fitImageToContainer(imageElement, viewerContainer)
        // Clean up object URL after loading
        URL.revokeObjectURL(objectUrl)
      }
      
      imageElement.onerror = () => {
        console.error('❌ Failed to load image')
        URL.revokeObjectURL(objectUrl) // Clean up on error
        imageElement.style.display = 'none'
        viewerContainer.innerHTML = `
          <div class="flex items-center justify-center h-full bg-gray-50 rounded-lg">
            <div class="text-center p-8">
              <div class="text-6xl mb-4">❌</div>
              <h3 class="text-lg font-semibold text-gray-900 mb-2">Failed to Load Image</h3>
              <p class="text-gray-600">Could not load the image document.</p>
            </div>
          </div>
        `
      }
      
      imageElement.src = objectUrl
    } catch (error) {
      console.error('Error loading image:', error)
      // Show error message
      viewerContainer.innerHTML = `
        <div class="flex items-center justify-center h-full bg-gray-50 rounded-lg">
          <div class="text-center p-8">
            <div class="text-6xl mb-4">❌</div>
            <h3 class="text-lg font-semibold text-gray-900 mb-2">Failed to Load Image</h3>
            <p class="text-gray-600">Could not load the image document.</p>
          </div>
        </div>
      `
    }
  }

  private fitImageToContainer(imageElement: HTMLImageElement, viewerContainer: HTMLElement): void {
    const contentContainer = viewerContainer.querySelector('.image-viewer-content') as HTMLElement
    if (!contentContainer) return

    const containerWidth = contentContainer.clientWidth - 40 // Account for padding
    const containerHeight = contentContainer.clientHeight - 40
    
    const imageWidth = imageElement.naturalWidth
    const imageHeight = imageElement.naturalHeight
    
    // Calculate scale to fit container
    const scaleX = containerWidth / imageWidth
    const scaleY = containerHeight / imageHeight
    const scale = Math.min(scaleX, scaleY, 1) // Don't scale up beyond 100%
    
    // Apply scale
    imageElement.style.width = `${imageWidth * scale}px`
    imageElement.style.height = `${imageHeight * scale}px`
    
    // Update zoom level display
    const zoomLevel = viewerContainer.querySelector('#image-zoom-level') as HTMLElement
    if (zoomLevel) {
      zoomLevel.textContent = `${Math.round(scale * 100)}%`
    }
    
    // Store scale for zoom controls
    imageElement.dataset.currentScale = scale.toString()
  }

  // @ts-ignore - Legacy method, replaced by ImageViewer class
  private setupImageViewerEventListeners(viewerContainer: HTMLElement, imageFile: any, skipAutoSelections: boolean): void {
    const imageElement = viewerContainer.querySelector('#image-display') as HTMLImageElement
    const zoomInBtn = viewerContainer.querySelector('#image-zoom-in')
    const zoomOutBtn = viewerContainer.querySelector('#image-zoom-out')
    const resetBtn = viewerContainer.querySelector('#image-reset-zoom')
    const zoomLevel = viewerContainer.querySelector('#image-zoom-level')
    
    if (!imageElement) return

    // Zoom controls
    zoomInBtn?.addEventListener('click', () => {
      const currentScale = parseFloat(imageElement.dataset.currentScale || '1')
      const newScale = Math.min(currentScale * 1.2, 5) // Max 500% zoom
      this.setImageZoom(imageElement, newScale, zoomLevel as HTMLElement | null)
    })

    zoomOutBtn?.addEventListener('click', () => {
      const currentScale = parseFloat(imageElement.dataset.currentScale || '1')
      const newScale = Math.max(currentScale / 1.2, 0.1) // Min 10% zoom
      this.setImageZoom(imageElement, newScale, zoomLevel as HTMLElement | null)
    })

    resetBtn?.addEventListener('click', () => {
      this.fitImageToContainer(imageElement, viewerContainer)
    })

    // Selection functionality (if not skipping)
    if (!skipAutoSelections) {
      this.setupImageSelectionListeners(imageElement, imageFile)
    }
  }

  private setImageZoom(imageElement: HTMLImageElement, scale: number, zoomLevel: HTMLElement | null): void {
    const naturalWidth = imageElement.naturalWidth
    const naturalHeight = imageElement.naturalHeight
    
    imageElement.style.width = `${naturalWidth * scale}px`
    imageElement.style.height = `${naturalHeight * scale}px`
    imageElement.dataset.currentScale = scale.toString()
    
    if (zoomLevel) {
      zoomLevel.textContent = `${Math.round(scale * 100)}%`
    }
  }

  private setupImageSelectionListeners(imageElement: HTMLImageElement, imageFile: any): void {
    let isSelecting = false
    let startX = 0
    let startY = 0
    let selectionOverlay: HTMLElement | null = null

    imageElement.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return // Only left click
      
      isSelecting = true
      const rect = imageElement.getBoundingClientRect()
      startX = e.clientX - rect.left
      startY = e.clientY - rect.top
      
      // Create selection overlay
      selectionOverlay = document.createElement('div')
      selectionOverlay.style.cssText = `
        position: absolute;
        border: 2px dashed #3b82f6;
        background: rgba(59, 130, 246, 0.1);
        pointer-events: none;
        z-index: 10;
      `
      imageElement.parentElement?.appendChild(selectionOverlay)
    })

    imageElement.addEventListener('mousemove', (e) => {
      if (!isSelecting || !selectionOverlay) return
      
      const rect = imageElement.getBoundingClientRect()
      const currentX = e.clientX - rect.left
      const currentY = e.clientY - rect.top
      
      const left = Math.min(startX, currentX)
      const top = Math.min(startY, currentY)
      const width = Math.abs(currentX - startX)
      const height = Math.abs(currentY - startY)
      
      selectionOverlay.style.left = `${left}px`
      selectionOverlay.style.top = `${top}px`
      selectionOverlay.style.width = `${width}px`
      selectionOverlay.style.height = `${height}px`
    })

    imageElement.addEventListener('mouseup', async (e) => {
      if (!isSelecting || !selectionOverlay) return
      
      isSelecting = false
      
      const rect = imageElement.getBoundingClientRect()
      const endX = e.clientX - rect.left
      const endY = e.clientY - rect.top
      
      const width = Math.abs(endX - startX)
      const height = Math.abs(endY - startY)
      
      // Only create selection if it's large enough
      if (width > 10 && height > 10) {
        const x = Math.min(startX, endX)
        const y = Math.min(startY, endY)
        
        // Create selection
        await this.createImageSelection(imageFile, x, y, width, height, imageElement)
      }
      
      // Remove selection overlay
      selectionOverlay?.remove()
      selectionOverlay = null
    })
  }

  private async createImageSelection(imageFile: any, x: number, y: number, width: number, height: number, imageElement: HTMLImageElement): Promise<void> {
    try {
      const currentProject = this.appState.getCurrentProject()
      if (!currentProject) {
        console.error('No current project for creating selection')
        return
      }

      // Calculate relative coordinates (0-1 range)
      const imageWidth = imageElement.naturalWidth
      const imageHeight = imageElement.naturalHeight
      const scale = parseFloat(imageElement.dataset.currentScale || '1')
      
      const relativeX = x / (imageWidth * scale)
      const relativeY = y / (imageHeight * scale)
      const relativeWidth = width / (imageWidth * scale)
      const relativeHeight = height / (imageHeight * scale)
      
      // Convert to absolute coordinates
      const absX = relativeX * imageWidth
      const absY = relativeY * imageHeight
      const absWidth = relativeWidth * imageWidth
      const absHeight = relativeHeight * imageHeight

      const selectionData = {
        project_id: currentProject.id,
        document_id: imageFile.id,
        document_type: 'image',
        x: absX,
        y: absY,
        width: absWidth,
        height: absHeight
        // page_number is optional and not needed for images
        // font_size is not part of SelectionCreate interface
      }

      console.log('🖼️ Creating image selection:', selectionData)
      
      // Add retry logic for rate limiting
      const response = await this.createSelectionWithRetry(selectionData)
      console.log('✅ Image selection created:', response)
      
            // Queue thumbnail generation using shared system
            if (this.imageThumbnailGenerator) {
              this.imageThumbnailGenerator.queueThumbnailGeneration(response.id, imageFile.id)
            }
      
      // Refresh selections gallery by loading current selections
      this.refreshImageSelectionsGallery(imageFile.id)
      
      // CRITICAL FIX: Refresh project data to update selection_count
      const project = this.appState.getCurrentProject()
      if (project) {
        console.log('🔄 Refreshing project data to update selection_count after image selection save')
        try {
          const updatedProject = await this.apiService.getProject(project.id)
          this.appState.setCurrentProject(updatedProject)
          console.log('✅ Project selection_count updated:', updatedProject.selection_count)
        } catch (error) {
          console.error('❌ Failed to refresh project data:', error)
        }
      }
      
    } catch (error) {
      console.error('❌ Failed to create image selection:', error)
      // Show user-friendly error message
      this.toast.error('Failed to create selection', 'Please try again in a moment. The server may be busy.')
    }
  }

  private async createSelectionWithRetry(selectionData: any, maxRetries: number = 3): Promise<any> {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await this.apiService.createSelection(selectionData)
      } catch (error: any) {
        const isRateLimit = error.response?.status === 429
        const isNetworkError = error.code === 'ERR_NETWORK' || error.message === 'Network Error'
        
        if ((isRateLimit || isNetworkError) && attempt < maxRetries) {
          // Wait with exponential backoff: 1s, 2s, 4s
          const waitTime = Math.pow(2, attempt - 1) * 1000
          console.log(`⏳ Rate limit or network error, retrying in ${waitTime}ms (attempt ${attempt}/${maxRetries})`)
          await new Promise(resolve => setTimeout(resolve, waitTime))
          continue
        }
        
        // If it's the last attempt or not a retryable error, throw
        throw error
      }
    }
  }

  private async deleteSelectionWithRetry(selectionId: string, maxRetries: number = 3): Promise<void> {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        await this.apiService.deleteSelection(selectionId)
        console.log('✅ Selection deleted successfully via API')
        return
      } catch (error: any) {
        const isRateLimit = error.response?.status === 429
        const isNetworkError = error.code === 'ERR_NETWORK' || error.message === 'Network Error'
        const isNotFound = error.response?.status === 404
        
        // If selection is not found (404), it's already deleted - that's fine
        if (isNotFound) {
          console.log('⚠️ Selection already deleted (404) - treating as success')
          return
        }
        
        if ((isRateLimit || isNetworkError) && attempt < maxRetries) {
          // Wait with exponential backoff: 1s, 2s, 4s
          const waitTime = Math.pow(2, attempt - 1) * 1000
          console.log(`⏳ Rate limit or network error during deletion, retrying in ${waitTime}ms (attempt ${attempt}/${maxRetries})`)
          await new Promise(resolve => setTimeout(resolve, waitTime))
          continue
        }
        
        // If it's the last attempt or not a retryable error, throw
        throw error
      }
    }
  }

  private async updateSelectionWithRetry(selectionId: string, data: any, maxRetries: number = 3): Promise<void> {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        await this.apiService.updateSelection(selectionId, data)
        return
      } catch (error: any) {
        const isRateLimit = error.response?.status === 429
        const isNetworkError = error.code === 'ERR_NETWORK' || error.message === 'Network Error'
        
        if ((isRateLimit || isNetworkError) && attempt < maxRetries) {
          // Wait with exponential backoff: 1s, 2s, 4s
          const waitTime = Math.pow(2, attempt - 1) * 1000
          console.log(`⏳ Rate limit or network error during update, retrying in ${waitTime}ms (attempt ${attempt}/${maxRetries})`)
          await new Promise(resolve => setTimeout(resolve, waitTime))
          continue
        }
        
        // If it's the last attempt or not a retryable error, throw
        throw error
      }
    }
  }

  private initializeImageSelectionsGallery(galleryContainer: HTMLElement, documentId: string): void {
    try {
      // Import SelectionsGallery dynamically (browser-compatible)
      import('../components/selections-gallery').then(({ SelectionsGallery }) => {
        this.createImageSelectionsGallery(galleryContainer, documentId, SelectionsGallery)
      }).catch(error => {
        console.error('Failed to import SelectionsGallery:', error)
      })
    } catch (error) {
      console.error('Failed to initialize image selections gallery:', error)
    }
  }

  private createImageSelectionsGallery(galleryContainer: HTMLElement, documentId: string, SelectionsGallery: any): void {
    try {
      
      // Create selections gallery instance
      const selectionsGallery = new SelectionsGallery(galleryContainer, {
        onSelectionReorder: (selections: any[]) => {
          console.log('🔄 Image selection reordered:', selections.length)
          // Update app state with reordered selections
          this.appState.setSelections(selections)
        },
        onSelectionRemove: async (id: string) => {
          console.log('🗑️ Removing image selection:', id)
          try {
            await this.deleteSelectionWithRetry(id)
            console.log('✅ Selection deleted successfully, refreshing gallery')
            
            // CRITICAL FIX: Refresh project data to update selection_count
            const currentProject = this.appState.getCurrentProject()
            if (currentProject) {
              console.log('🔄 Refreshing project data to update selection_count after image selection deletion')
              try {
                const updatedProject = await this.apiService.getProject(currentProject.id)
                this.appState.setCurrentProject(updatedProject)
                console.log('✅ Project selection_count updated after image selection deletion:', updatedProject.selection_count)
              } catch (error) {
                console.error('❌ Failed to refresh project data after image selection deletion:', error)
              }
            }
            
            // Add small delay to ensure database consistency
            setTimeout(() => {
              this.refreshImageSelectionsGallery(documentId)
            }, 100)
          } catch (error: any) {
            console.error('Failed to delete selection:', error)
            
            // Check if it's a 404 error (selection already deleted)
            if (error.response?.status === 404) {
              console.log('⚠️ Selection already deleted (404), refreshing gallery anyway')
              // Selection was already deleted, just refresh the gallery
              this.refreshImageSelectionsGallery(documentId)
            } else {
              // Show user-friendly error message for other errors
              this.toast.error('Failed to delete selection', 'Please try again in a moment. The server may be busy.')
            }
          }
        },
        onSelectionNavigate: (id: string) => {
          console.log('🎯 Navigate to image selection:', id)
          // For images, we could scroll to the selection or highlight it
        },
        onRetryThumbnail: (id: string) => {
          console.log('🔄 Retry thumbnail callback for image selection:', id)
          if (this.imageThumbnailGenerator) {
            this.imageThumbnailGenerator.queueThumbnailGeneration(id, documentId)
          }
        },
        getPDFName: (docId: string) => {
          // Return document name for display
          const documents = this.appState.getProjectDocuments()
          const doc = documents.find(d => d.id === docId)
          return doc ? doc.original_filename : 'Unknown Document'
        }
      })
      
      // Store the gallery instance on the container for later access
      ;(galleryContainer as any).selectionsGallery = selectionsGallery
      
      // Load selections for this document
      this.loadImageSelections(documentId, selectionsGallery)
      
    } catch (error) {
      console.error('Failed to create image selections gallery:', error)
    }
  }

  private async refreshImageSelectionsGallery(documentId: string): Promise<void> {
    console.log('🔄 refreshImageSelectionsGallery called for document:', documentId)
    try {
      // Get current project
      const currentProject = this.appState.getCurrentProject()
      if (!currentProject) {
        console.error('No current project found')
        return
      }
      
      // Load selections for this document
      const response = await this.apiService.listProjectSelections(currentProject.id)
      const selections = response.selections || []
      const documentSelections = selections.filter((s: any) => s.document_id === documentId)
      
      console.log(`📋 Loaded ${documentSelections.length} selections for image document`)
      
      // Find the existing gallery and update it
      const galleryContainer = document.querySelector('#modal-selections-gallery')
      console.log('🔍 Gallery container found:', !!galleryContainer)
      console.log('🔍 Gallery instance found:', !!(galleryContainer && (galleryContainer as any).selectionsGallery))
      
      if (galleryContainer && (galleryContainer as any).selectionsGallery) {
        const gallery = (galleryContainer as any).selectionsGallery
        console.log('🔄 Updating gallery with selections:', documentSelections.length)
        gallery.updateSelections(documentSelections)
        console.log('✅ Gallery updated successfully')
      } else {
        console.warn('⚠️ Gallery container or instance not found, cannot refresh')
      }
      
    } catch (error) {
      console.error('Failed to refresh image selections gallery:', error)
    }
  }

  private async loadImageSelections(documentId: string, selectionsGallery: any): Promise<void> {
    try {
      const currentProject = this.appState.getCurrentProject()
      if (!currentProject) return
      
      // Load selections for this document
      const response = await this.apiService.listProjectSelections(currentProject.id)
      const selections = response.selections || []
      const documentSelections = selections.filter((s: any) => s.document_id === documentId)
      
      console.log(`📋 Loaded ${documentSelections.length} selections for image document`)
      
      // Update gallery with selections
      selectionsGallery.updateSelections(documentSelections)
      
    } catch (error) {
      console.error('Failed to load image selections:', error)
    }
  }



  private setupModalFooterListeners(modalContent: HTMLElement): void {
    const cancelBtn = modalContent.querySelector('#modal-cancel-btn')
    const acceptBtn = modalContent.querySelector('#modal-accept-btn')
    
    // Cancel button - close modal without saving
    cancelBtn?.addEventListener('click', () => {
      // Close the modal
      const modal = document.querySelector('.modal-backdrop')
      if (modal) {
        modal.classList.remove('modal-open')
        setTimeout(() => {
          modal.remove()
        }, 300)
      }
    })
    
    // Accept button - save selections and close modal
    acceptBtn?.addEventListener('click', async () => {
      // Get current selections - handle both PDF and image selections
      let selections: any[] = []
      let selectionsCount = 0
      const modalManager = this
      const galleryContainer = document.querySelector('#modal-selections-gallery')
      
      // CRITICAL FIX: Check ImageViewer selections first (for image documents)
      const imageViewer = document.querySelector('#modal-pdf-viewer') as any
      if (imageViewer && imageViewer.imageViewer && imageViewer.imageViewer.getSelections) {
        selections = imageViewer.imageViewer.getSelections()
        selectionsCount = selections.length
        console.log('📸 Found image selections:', selectionsCount)
      } else if (modalManager.documentViewer && modalManager.documentViewer.getSelections) {
        // Use the modal's document viewer instance
        selections = modalManager.documentViewer.getSelections()
        selectionsCount = selections.length
      } else if ((window as any).pdfViewerRefactored && (window as any).pdfViewerRefactored.selectionManager) {
        // Fallback to global refactored PDF viewer
        selections = (window as any).pdfViewerRefactored.selectionManager.getSelections()
        selectionsCount = selections.length
      } else if ((window as any).pdfViewer) {
        // Fallback to legacy PDF viewer
        selections = (window as any).pdfViewer.getSelections()
        selectionsCount = selections.length
      } else if (galleryContainer && (galleryContainer as any).selectionsGallery) {
        // Image selections from gallery
        const gallery = (galleryContainer as any).selectionsGallery
        selections = gallery.getSelections ? gallery.getSelections() : []
        selectionsCount = selections.length
      }
      
      if (selectionsCount === 0) {
        this.toast.warning('No Selections', 'Please select some text regions first')
        return
      }
      
      // Get current document and project info
      const currentFile = this.appState.getCurrentFile()
      const currentProject = this.appState.getCurrentProject()
      
      if (!currentFile || !currentProject) {
        this.toast.error('Missing Information', 'No document or project loaded')
        return
      }
      
      try {
        // Save each selection to the backend
        const savePromises = selections.map(async (selection) => {
          // Always save selections created in modal (they're not auto-saved)
          console.log(`Processing selection for save:`, selection.id)
          
          // Create selection data for API
          // Determine document type from filename extension
          const filename = currentFile.filename || ''
          let documentType: 'pdf' | 'image' | 'manual' = 'pdf'
          if (/\.(jpg|jpeg|png|gif|bmp|tiff|webp)$/i.test(filename)) {
            documentType = 'image'
          }
          
          const selectionData = {
            project_id: currentProject.id,
            document_id: currentFile.id,
            document_type: documentType,
            x: selection.x,
            y: selection.y,
            width: selection.width,
            height: selection.height,
            page_number: selection.page_number || selection.pageNumber || 1,
            thumbnail_data: selection.thumbnail_data
          }
          
          console.log('Saving selection:', selectionData)
          const savedSelection = await this.apiService.createSelection(selectionData)
          
          // Update app state with saved selection
          this.appState.addSelection(savedSelection)
          
          // If selection has thumbnail_data, backend will auto-OCR
          // Otherwise, trigger manual OCR processing
          if (!selection.thumbnail_data || selection.thumbnail_data === 'loading') {
            const processingManager = (window as any).processingManager
            if (processingManager && processingManager.processIndividualOCR) {
              console.log('🚀 Triggering manual OCR (no thumbnail):', savedSelection.id)
              processingManager.processIndividualOCR(savedSelection, false).catch((error: any) => {
                console.error('Failed to trigger OCR:', error)
              })
            }
          } else {
            console.log('✅ Selection has thumbnail, backend will auto-OCR:', savedSelection.id)
            
            // Fallback: Also trigger frontend OCR processing as backup
            const processingManager = (window as any).processingManager
            if (processingManager && processingManager.processIndividualOCR) {
              console.log('🔄 Triggering frontend OCR as backup:', savedSelection.id)
              processingManager.processIndividualOCR(savedSelection, false).catch((error: any) => {
                console.error('Failed to trigger backup OCR:', error)
              })
            }
          }
          
          return savedSelection
        })
        
        // Close modal immediately for better UX
        const modal = document.querySelector('.modal-backdrop')
        if (modal) {
          modal.classList.remove('modal-open')
          setTimeout(() => {
            modal.remove()
          }, 300)
        }
        
        // Wait for all selections to save
        await Promise.all(savePromises)
        
        // Show success message
        this.toast.success('Selections Saved', `${selectionsCount} text region${selectionsCount > 1 ? 's' : ''} saved successfully`)
        
        // Trigger re-render by dispatching a custom event
        window.dispatchEvent(new CustomEvent('modal-closed', { detail: { type: 'document-upload' } }))
        
      } catch (error) {
        console.error('Failed to save selections:', error)
        this.toast.error('Save Failed', 'Failed to save selections to database')
      }
    })
    
    // Update selections count in real-time
    this.setupSelectionCountUpdater(modalContent)
    
    // Setup image upload options
    this.setupImageUploadOptions(modalContent)
  }

  /**
   * Generate a unique selection ID
   */
  private generateSelectionId(): string {
    return 'temp_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
  }

  /**
   * Show image upload options after an image is uploaded
   */
  private showImageUploadOptions(modalContent: HTMLElement): void {
    const imageOptions = modalContent.querySelector('#modal-image-options')
    if (imageOptions) {
      imageOptions.classList.remove('hidden')
      console.log('🖼️ Showing image upload options')
    }
  }

  private setupImageUploadOptions(modalContent: HTMLElement): void {
    const useFullImageBtn = modalContent.querySelector('#modal-use-full-image')
    const selectFromImageBtn = modalContent.querySelector('#modal-select-from-image')
    
    console.log('🔍 Setting up image upload options:', {
      useFullImageBtn: !!useFullImageBtn,
      selectFromImageBtn: !!selectFromImageBtn,
      modalContent: !!modalContent
    })
    
    // Use Full Image button
    useFullImageBtn?.addEventListener('click', async () => {
      try {
        console.log('🖼️ Use Full Image clicked - NEW VERSION')
        console.log('🔍 Button element:', useFullImageBtn)
        console.log('🔍 Modal content:', modalContent)
        
        // Get the current image document FIRST
        const currentFile = this.appState.getCurrentFile() as any
        console.log('🔍 Current file from app state:', currentFile)
        
        if (!currentFile || currentFile.document_type !== 'image') {
          console.error('❌ No image document found:', {
            currentFile,
            documentType: currentFile?.document_type,
            hasCurrentFile: !!currentFile
          })
          this.toast.error('No Image', 'No image document found')
          return
        }
        
        // Get the actual image dimensions and thumbnail from the ImageViewer BEFORE closing modal
        const imageViewer = document.querySelector('#modal-pdf-viewer') as any
        let imageWidth = 1
        let imageHeight = 1
        let thumbnailData = null
        
        if (imageViewer && imageViewer.imageViewer && imageViewer.imageViewer.imageElement) {
          const imageElement = imageViewer.imageViewer.imageElement
          imageWidth = imageElement.naturalWidth || 1
          imageHeight = imageElement.naturalHeight || 1
          console.log('🖼️ Got image dimensions from ImageViewer:', { width: imageWidth, height: imageHeight })
          
          // Generate thumbnail data BEFORE closing modal
          if (imageViewer.imageViewer.getThumbnail) {
            try {
              console.log('🖼️ Generating thumbnail BEFORE closing modal')
              const tempSelection = {
                id: 'temp',
                x: 0,
                y: 0,
                width: imageWidth,
                height: imageHeight,
                page_number: 1
              }
              thumbnailData = await imageViewer.imageViewer.getThumbnail(tempSelection)
              console.log('✅ Thumbnail generated BEFORE closing modal:', thumbnailData ? 'success' : 'failed')
            } catch (thumbnailError) {
              console.error('❌ Failed to generate thumbnail before closing modal:', thumbnailError)
            }
          }
        } else {
          console.warn('⚠️ Could not get image dimensions from ImageViewer, using fallback 1x1')
        }
        
        // Show immediate loading feedback before closing modal
        console.log('🚀 NEW: Showing immediate loading feedback')
        this.toast.info('Processing...', 'Creating full image selection...')
        
        // Close modal AFTER getting dimensions and thumbnail
        console.log('🚪 NEW: Closing modal after getting image dimensions and thumbnail')
        const modal = document.querySelector('.modal-backdrop')
        if (modal) {
          console.log('✅ NEW: Modal found, closing immediately...')
          modal.classList.remove('modal-open')
          document.body.classList.remove('modal-open')
          // Remove modal from DOM immediately to prevent timing issues
          modal.remove()
          console.log('✅ NEW: Modal removed from DOM immediately')
        } else {
          console.warn('⚠️ NEW: Modal not found for closing')
        }
        
        // Create a selection for the entire image using actual dimensions
        const fullImageSelection = {
          id: this.generateSelectionId(),
          project_id: currentFile.project_id || this.appState.getCurrentProject()?.id || '',
          document_id: currentFile.id,
          document_type: 'image',
          x: 0,
          y: 0,
          width: imageWidth, // Use actual image width
          height: imageHeight, // Use actual image height
          page_number: 1,
          extracted_text: '',
          ocr_confidence: 0,
          thumbnail_data: 'loading'
        }
        
        console.log('📄 Creating full image selection:', fullImageSelection)
        
        // Save the selection to backend with pre-generated thumbnail data
        const selectionData = {
          project_id: fullImageSelection.project_id,
          document_id: fullImageSelection.document_id,
          document_type: 'image' as 'image' | 'pdf' | 'manual',
          x: 0,
          y: 0,
          width: imageWidth,
          height: imageHeight,
          page_number: 1,
          thumbnail_data: thumbnailData // Include pre-generated thumbnail data
        }
        
        console.log('🔍 Calling API with selection data (including thumbnail):', selectionData)
        
        // Create a temporary loading selection for immediate UI feedback
        const tempLoadingSelection = {
          id: 'temp-loading-' + Date.now(),
          project_id: selectionData.project_id,
          document_id: selectionData.document_id,
          document_type: 'image' as const,
          x: 0,
          y: 0,
          width: imageWidth,
          height: imageHeight,
          page_number: 1,
          thumbnail_data: 'loading',
          processed: 'processing' as const,
          extracted_text: '',
          created_at: new Date().toISOString()
        }
        
        // Add temporary loading selection to show immediate feedback
        console.log('🚀 Adding temporary loading selection for immediate feedback')
        this.appState.addSelection(tempLoadingSelection)
        
        const response = await this.apiService.createSelection(selectionData)
        
        console.log('✅ Full image selection created with thumbnail:', response)
        
        // Replace temporary loading selection with real selection
        const selectionWithThumbnail = {
          ...response,
          thumbnail_data: thumbnailData, // Ensure thumbnail data is immediately available
          processed: 'pending' as const // Ensure processed status is set correctly
        }
        console.log('🔍 Replacing temporary loading selection with real selection:', {
          id: selectionWithThumbnail.id,
          hasThumbnail: !!selectionWithThumbnail.thumbnail_data,
          thumbnailLength: selectionWithThumbnail.thumbnail_data?.length || 0,
          processed: selectionWithThumbnail.processed
        })
        
        // Remove temporary loading selection and add real one
        this.appState.removeSelection(tempLoadingSelection.id)
        this.appState.addSelection(selectionWithThumbnail)
        
        this.toast.success('Success', 'Full image added as selection')
        
        // CRITICAL FIX: Trigger OCR processing for the full image selection (async, don't wait)
        try {
          console.log('🚀 Triggering OCR processing for full image selection:', response.id)
          // Trigger OCR processing via direct API call since the method might not be available
          const ocrResponse = await fetch(`https://api.staging.doc2plot.com/api/selections/${response.id}/ocr/start`, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json'
            }
          })
          
          if (ocrResponse.ok) {
            const ocrResult = await ocrResponse.json()
            console.log('✅ OCR processing triggered for full image selection:', ocrResult)
          } else {
            console.error('❌ Failed to trigger OCR - HTTP error:', ocrResponse.status)
          }
        } catch (ocrError) {
          console.error('❌ Failed to trigger OCR for full image selection:', ocrError)
        }
        
        
      } catch (error) {
        console.error('❌ Failed to create full image selection:', error)
        const errorMessage = error instanceof Error ? error.message : 'Unknown error'
        console.error('❌ Error details:', {
          message: errorMessage,
          stack: error instanceof Error ? error.stack : undefined,
          name: error instanceof Error ? error.name : 'Unknown'
        })
        this.toast.error('Error', 'Failed to process full image: ' + errorMessage)
      }
    })
    
    // Select from Image button
    selectFromImageBtn?.addEventListener('click', () => {
      console.log('✂️ Select from Image clicked')
      
      // Hide the image options and show the image viewer
      const imageOptions = modalContent.querySelector('#modal-image-options')
      if (imageOptions) {
        imageOptions.classList.add('hidden')
      }
      
      // Get the current image document and initialize the image viewer
      const currentFile = this.appState.getCurrentFile() as any
      if (currentFile && currentFile.document_type === 'image') {
        this.initializeModalImageViewer(currentFile, false, false)
      }
    })
  }

  /**
   * Update selection count display and accept button state
   */
  private updateSelectionCount(modalContent: HTMLElement, count: number): void {
    const countElement = modalContent.querySelector('#modal-selections-count')
    const acceptBtn = modalContent.querySelector('#modal-accept-btn') as HTMLButtonElement
    
    if (countElement) {
      countElement.textContent = `${count} selection${count !== 1 ? 's' : ''}`
    }
    
    if (acceptBtn) {
      acceptBtn.disabled = count === 0
      acceptBtn.textContent = count === 0 ? 'No Selections' : `✓ Accept ${count} Selection${count !== 1 ? 's' : ''}`
    }
  }

  private setupSelectionCountUpdater(modalContent: HTMLElement): void {
    // Store modal opened time for tracking new selections
    const modalOpenedTime = Date.now()
    ;(modalContent as any).modalOpenedTime = modalOpenedTime
    
    // Track selections created in this modal session
    let sessionSelections: any[] = []
    
    // Listen for selection creation events
    const handlePDFSelectionCreated = (event: CustomEvent) => {
      console.log('📋 PDF selection created in modal:', event.detail.selection.id)
      sessionSelections.push(event.detail.selection)
      this.updateSelectionCount(modalContent, sessionSelections.length)
    }
    
    const handleImageSelectionCreated = (event: CustomEvent) => {
      console.log('📋 Image selection created in modal:', event.detail.selection.id)
      sessionSelections.push(event.detail.selection)
      this.updateSelectionCount(modalContent, sessionSelections.length)
    }
    
    window.addEventListener('pdf-selection-created', handlePDFSelectionCreated as EventListener)
    window.addEventListener('image-selection-created', handleImageSelectionCreated as EventListener)
    
    const updateCount = () => {
      const galleryContainer = document.querySelector('#modal-selections-gallery')
      
      let count = sessionSelections.length
      
      // If no session selections, check for selections that were just created in this session
      if (count === 0) {
        // Check for temporary selections (newly created, not yet saved)
        const tempSelections = document.querySelectorAll('.temp-selection, .selection-preview')
        count = tempSelections.length
        
        // If no temp selections, check for selections that were just created in this session
        if (count === 0) {
          // Check image viewer selections first (for image documents)
          const imageViewer = document.querySelector('#modal-pdf-viewer') as any
          if (imageViewer && imageViewer.imageViewer && imageViewer.imageViewer.getSelections) {
            const imageSelections = imageViewer.imageViewer.getSelections()
            count = imageSelections.length
          }
          
          // If no image selections, check gallery selections
          if (count === 0 && galleryContainer && (galleryContainer as any).selectionsGallery) {
            const gallery = (galleryContainer as any).selectionsGallery
            const allSelections = gallery.getSelections ? gallery.getSelections() : []
            
            // Filter to only recent selections (created in current session)
            const recentSelections = allSelections.filter((selection: any) => {
              // If selection has a creation timestamp, use it
              if (selection.created_at) {
                const createdTime = new Date(selection.created_at).getTime()
                return createdTime > modalOpenedTime
              }
              // Otherwise, assume it's recent if it exists (fallback)
              return true
            })
            
            count = recentSelections.length
          }
        }
      }
      
      this.updateSelectionCount(modalContent, count)
    }
    
    // Update immediately
    updateCount()
    
    // Update periodically (every 500ms)
    const interval = setInterval(updateCount, 500)
    
    // Clean up interval and event listeners when modal closes
    const modal = document.querySelector('.modal-backdrop')
    if (modal) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            const target = mutation.target as HTMLElement
            if (!target.classList.contains('modal-open')) {
              clearInterval(interval)
              window.removeEventListener('pdf-selection-created', handlePDFSelectionCreated as EventListener)
              window.removeEventListener('image-selection-created', handleImageSelectionCreated as EventListener)
              observer.disconnect()
            }
          }
        })
      })
      
      observer.observe(modal, { attributes: true })
    }
  }

  private setupManualEntryEventListeners(modalContent: HTMLElement, modal?: any): void {
    const cancelBtn = modalContent.querySelector('#modal-manual-cancel')
    const saveBtn = modalContent.querySelector('#modal-manual-save')
    const textInput = modalContent.querySelector('#manual-text-input') as HTMLTextAreaElement
    
    cancelBtn?.addEventListener('click', () => {
      // Close modal using modal instance
      if (modal && modal.close) {
        modal.close()
      }
    })
    
    saveBtn?.addEventListener('click', async () => {
      const text = textInput.value.trim()
      if (!text) {
        this.toast.error('Empty Text', 'Please enter some text before saving')
        return
      }
      
      // Check if we're editing an existing manual entry
      const documentId = (saveBtn as HTMLElement).dataset.documentId
      console.log('💾 Manual entry save button clicked:', { 
        documentId, 
        textLength: text.length, 
        isEditing: !!documentId,
        saveButtonElement: saveBtn,
        dataset: (saveBtn as HTMLElement).dataset,
        allAttributes: Array.from((saveBtn as HTMLElement).attributes).map(attr => `${attr.name}="${attr.value}"`)
      })
      
      if (documentId) {
        // Update existing manual text document
        console.log('🔄 Updating existing manual text document:', documentId)
        await this.dataManager.saveManualTextEdit(documentId, text, modal)
      } else {
        // Create new manual entry
        console.log('➕ Creating new manual entry')
        await this.dataManager.saveManualEntry(text, modal)
      }
    })
  }
}
